import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';

const ACTION_LABELS = { INSERT: 'Yeni Kayıt', UPDATE: 'Güncelleme', DELETE: 'Silme' };
const ACTION_COLORS = { INSERT: '#10b981', UPDATE: '#3b82f6', DELETE: '#ef4444' };

const TABLE_LABELS = {
  stocks: 'Stoklar',
  accounts: 'Cari Hesaplar',
  invoices: 'Faturalar (eski)',
  sales_invoices: 'Satış Faturaları',
  purchase_invoices: 'Alış Faturaları',
  waybills: 'İrsaliyeler (eski)',
  sales_waybills: 'Satış İrsaliyeleri',
  purchase_waybills: 'Alış İrsaliyeleri',
  warehouses: 'Depolar',
  personnel: 'Personel',
  'bank-accounts': 'Banka Hesapları',
  'cash-registers': 'Kasalar',
  'expense-cards': 'Masraf Kartları',
  colors: 'Renkler',
  sizes: 'Ebatlar',
  settings: 'Ayarlar',
  exchange: 'Dövizler',
  auth: 'Kimlik Doğrulama',
  brands: 'Markalar',
  units: 'Birimler',
  'stock-categories': 'Stok Kategorileri',
  'account-categories': 'Cari Kategorileri',
  'account-groups': 'Cari Grupları',
  'price-lists': 'Fiyat Listeleri',
  projects: 'Projeler',
  'special-codes': 'Özel Kodlar',
  regions: 'Bölgeler',
  carriers: 'Kargo Firmaları',
  'bank-definitions': 'Banka Tanımları',
  'audit-log': 'İşlem Geçmişi',
  users: 'Kullanıcılar',
  roles: 'Roller',
  companies: 'Firmalar',
  currencies: 'Para Birimleri',
  'exchange-rates': 'Döviz Kurları',
  translations: 'Çeviriler',
  'login-attempts': 'Giriş Denemeleri',
  'cari-transactions': 'Cari Dekont/Fiş',
  transactions: 'Hesap Hareketi',
  'bank-defs': 'Banka Tanımı',
  models: 'Modeller',
  groups: 'Gruplar',
};

function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('tr-TR') + ' ' + dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// new_data JSON'ından anlamlı bir "etiket" çıkar (kod + ad gibi)
function extractLabel(newData, tableName) {
  if (!newData) return null;
  let body;
  try { body = typeof newData === 'string' ? JSON.parse(newData) : newData; }
  catch (_) { return null; }
  if (!body || typeof body !== 'object') return null;

  const code = body.code || body.document_no || body.invoice_number;
  let name = null;

  if (tableName === 'personnel') {
    name = (body.first_name && body.last_name)
      ? `${body.first_name} ${body.last_name}`
      : (body.full_name || body.first_name || body.last_name);
  } else if (tableName === 'accounts') {
    name = body.title || body.name;
  } else if (tableName === 'invoices') {
    name = body.invoice_number || body.document_number || body.description;
  } else if (tableName === 'bank-accounts') {
    name = body.account_name || body.bank_name;
  } else if (tableName === 'cash-registers') {
    name = body.register_name;
  } else if (tableName === 'expense-cards') {
    name = body.card_name;
  } else if (tableName === 'cari-transactions') {
    // Cari dekont/fiş: transaction_type + amount + açıklama
    const txMap = {
      opening: 'Açılış Fişi',
      debit: 'Borç Dekontu',
      credit: 'Alacak Dekontu',
      virman: 'Virman Dekontu',
    };
    const txLabel = txMap[body.transaction_type] || body.transaction_type || 'İşlem';
    const amt = body.tx_amount || body.amount;
    const desc = body.description || '';
    return `${txLabel}${amt ? ' — ' + amt + ' ' + (body.tx_currency || '') : ''}${desc ? ' | ' + desc : ''}`;
  } else if (tableName === 'transactions') {
    // Banka hareketi
    const txMap = {
      opening: 'Açılış Fişi',
      credit: 'Alacak Dekontu',
      debit: 'Borç Dekontu',
      virman: 'Virman',
      income: 'Gelir',
      expense: 'Gider',
    };
    const txLabel = txMap[body.transaction_type] || body.transaction_type || 'Hareket';
    const amt = body.amount;
    const desc = body.description || '';
    return `${txLabel}${amt ? ' — ' + amt : ''}${desc ? ' | ' + desc : ''}`;
  } else if (tableName === 'settings') {
    name = body.value != null ? String(body.value).slice(0, 40) : body.setting_key;
    return body.setting_key ? `${body.setting_key}: ${name}` : name;
  } else {
    name = body.name || body.title || body.full_name || body.description || body.register_name || body.card_name;
  }

  if (code && name) return `${code} - ${name}`;
  if (name) return name;
  if (code) return code;
  return null;
}

// Anlaşılır alan etiketleri
const FIELD_LABELS = {
  code: 'Kod', name: 'Ad', title: 'Unvan', full_name: 'Tam Ad',
  first_name: 'Ad', last_name: 'Soyad',
  account_name: 'Hesap Adı', bank_name: 'Banka', branch_name: 'Şube',
  register_name: 'Kasa Adı', card_name: 'Kart Adı',
  invoice_number: 'Fatura No', document_no: 'Belge No', document_number: 'Belge No',
  description: 'Açıklama', notes: 'Notlar', amount: 'Tutar',
  transaction_date: 'İşlem Tarihi', invoice_date: 'Fatura Tarihi', due_date: 'Vade',
  email: 'E-posta', phone: 'Telefon', mobile: 'Mobil', address: 'Adres',
  city: 'Şehir', district: 'İlçe', country: 'Ülke',
  tax_office: 'Vergi Dairesi', tax_number: 'Vergi No', id_number: 'TC/Kimlik No',
  type: 'Tip', is_active: 'Aktif',
  currency_code: 'Para Birimi', vat_rate: 'KDV (%)',
  min_stock: 'Min. Stok', max_stock: 'Max. Stok',
  barcode: 'Barkod', department: 'Departman', position: 'Pozisyon',
  setting_key: 'Ayar', value: 'Değer',
  reference_no: 'Referans No', register_id: 'Kasa',
  account_id: 'Hesap', warehouse_id: 'Depo',
  // Cari dekont alanları
  transaction_type: 'İşlem Türü', tx_currency: 'İşlem Dövizi',
  tx_amount: 'İşlem Tutarı', tx_rate: 'Kur',
  target_account_id: 'Hedef Cari', target_tx_amount: 'Hedef Tutar',
  target_tx_rate: 'Hedef Kur',
  opening_direction: 'Açılış Yönü',
  date: 'Tarih',
};

function renderBodyGrid(bodyStr) {
  if (!bodyStr) return null;
  let body;
  try { body = typeof bodyStr === 'string' ? JSON.parse(bodyStr) : bodyStr; }
  catch (_) { return <pre style={{whiteSpace:'pre-wrap'}}>{bodyStr}</pre>; }
  if (!body || typeof body !== 'object') return null;

  const entries = Object.entries(body).filter(([k, v]) => {
    if (v === null || v === undefined || v === '' || v === 0 || v === false) return false;
    if (typeof v === 'object') return false;
    return true;
  });

  if (entries.length === 0) return <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Ek bilgi yok</span>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '0.8rem' }}>
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>{FIELD_LABELS[k] || k}:</div>
          <div style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{String(v)}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

function todayStr() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  const [dateFrom, setDateFrom] = useState(daysAgo(7));
  const [dateTo, setDateTo] = useState(todayStr());
  const [userId, setUserId] = useState('');
  const [tableName, setTableName] = useState('');
  const [action, setAction] = useState('');

  const [meta, setMeta] = useState({ tables: [], users: [], actions: [] });
  const [detail, setDetail] = useState(null);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await api.get('/audit-log/meta');
      if (res.data.success) setMeta(res.data.data);
    } catch (_) {}
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/audit-log', {
        params: { date_from: dateFrom, date_to: dateTo, user_id: userId || undefined,
                  table_name: tableName || undefined, action: action || undefined,
                  page, limit }
      });
      if (res.data.success) {
        setItems(res.data.data.items);
        setTotal(res.data.data.total);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, userId, tableName, action, page, limit]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const applyFilter = () => { setPage(1); fetchList(); };
  const clearFilter = () => {
    setDateFrom(daysAgo(7)); setDateTo(todayStr());
    setUserId(''); setTableName(''); setAction(''); setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const INVOICE_TYPE_LABELS = {
    sales:                  'Satış',
    retail_sale:            'Perakende Satış',
    sales_return:           'Satış İade',
    retail_sale_return:     'Perakende İade',
    purchase:               'Alış',
    purchase_return:        'Alış İade',
    sales_waybill:          'Satış İrsaliyesi',
    sales_waybill_return:   'Satış İade İrsaliyesi',
    purchase_waybill:       'Alış İrsaliyesi',
    purchase_waybill_return:'Alış İade İrsaliyesi',
  };
  const tableLabel = (t, item) => {
    // invoices/sales_invoices/purchase_invoices/sales_waybills/purchase_waybills için invoice_type alt etiket
    const splitTables = ['invoices','sales_invoices','purchase_invoices','sales_waybills','purchase_waybills','waybills'];
    if (splitTables.includes(t) && item) {
      try {
        const data = JSON.parse(item.new_data || item.old_data || '{}');
        const sub = INVOICE_TYPE_LABELS[data.invoice_type];
        const base = TABLE_LABELS[t] || t;
        if (sub) return `${base} (${sub})`;
      } catch (_) {}
    }
    const tr = TABLE_LABELS[t];
    return tr ? `${t} (${tr})` : t;
  };

  const prettyBody = (json) => {
    if (!json) return '-';
    try { return JSON.stringify(JSON.parse(json), null, 2); } catch (_) { return json; }
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>İşlem Geçmişi (LOG)</h2>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
            Kullanıcıların kayıt ekleme, güncelleme ve silme işlemleri
          </div>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Toplam: <b>{total.toLocaleString('tr-TR')}</b> kayıt
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
        borderRadius: 8, padding: 12, marginBottom: 12,
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10
      }}>
        <div>
          <label style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Başlangıç Tarihi</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Bitiş Tarihi</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Kullanıcı</label>
          <select value={userId} onChange={e => setUserId(e.target.value)} style={inputStyle}>
            <option value="">Tümü</option>
            {meta.users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>Modül</label>
          <select value={tableName} onChange={e => setTableName(e.target.value)} style={inputStyle}>
            <option value="">Tümü</option>
            {meta.tables.map(t => <option key={t} value={t}>{tableLabel(t)}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: 4 }}>İşlem</label>
          <select value={action} onChange={e => setAction(e.target.value)} style={inputStyle}>
            <option value="">Tümü</option>
            {meta.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <button onClick={applyFilter} style={{ ...btnStyle, background: 'var(--accent-primary)', color: '#fff', flex: 1 }}>Filtrele</button>
          <button onClick={clearFilter} style={{ ...btnStyle, flex: 1 }}>Temizle</button>
        </div>
      </div>

      {/* Quick date presets */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Bugün', from: todayStr(), to: todayStr() },
          { label: 'Son 7 gün', from: daysAgo(7), to: todayStr() },
          { label: 'Son 30 gün', from: daysAgo(30), to: todayStr() },
          { label: 'Son 90 gün', from: daysAgo(90), to: todayStr() },
        ].map(p => (
          <button key={p.label}
            onClick={() => { setDateFrom(p.from); setDateTo(p.to); setPage(1); }}
            style={{ ...btnStyle, fontSize: '0.75rem', padding: '4px 10px' }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 8, overflow: 'hidden' }}>
        <DataTable
          pageKey="audit-log-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={items}
          rowKey="id"
          columns={[
            { id: 'created_at', header: 'Tarih / Saat', accessor: 'created_at', sortable: true, width: 150,
              sortFn: (a,b) => new Date(a.created_at||0)-new Date(b.created_at||0),
              cell: it => fmtDate(it.created_at) },
            { id: 'user', header: 'Kullanıcı', accessor: it => (it.user_name||'') + ' ' + (it.username||''), sortable: true, width: 160,
              cell: it => <div><div style={{ fontWeight: 600 }}>{it.user_name || '-'}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{it.username}</div></div> },
            { id: 'table', header: 'Modül', accessor: it => tableLabel(it.table_name, it), sortable: true, width: 180 },
            { id: 'action', header: 'İşlem', accessor: it => ACTION_LABELS[it.action] || it.action, sortable: true, width: 100,
              cell: it => <span style={{ background: ACTION_COLORS[it.action] + '22', color: ACTION_COLORS[it.action], padding: '3px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>{ACTION_LABELS[it.action] || it.action}</span> },
            { id: 'record', header: 'Kayıt', accessor: it => extractLabel(it.new_data, it.table_name) || '', sortable: true,
              cell: it => { const label = extractLabel(it.new_data, it.table_name); return <><div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{label || '-'}</div>{it.record_id && <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{it.record_id}</div>}</>; } },
            { id: 'ip', header: 'IP', accessor: 'ip_address', sortable: true, width: 130,
              cellStyle: { fontSize: '0.72rem', color: 'var(--text-tertiary)' },
              cell: it => it.ip_address || '-' },
            { id: 'detail', header: 'Detay', sortable: false, hideable: false, draggable: false, filterable: false, width: 100,
              cell: it => <button onClick={(e) => { e.stopPropagation(); setDetail(it); }} style={{ border: '1px solid var(--border-secondary)', background: 'transparent', borderRadius: 4, padding: '3px 10px', fontSize: '0.72rem', cursor: 'pointer', color: 'var(--text-secondary)' }}>Görüntüle</button> },
          ]}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, borderTop: '1px solid var(--border-secondary)' }}>
            <button disabled={page <= 1} onClick={() => setPage(1)} style={btnStyle}>«</button>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={btnStyle}>‹</button>
            <span style={{ fontSize: '0.8rem', padding: '0 10px' }}>Sayfa {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={btnStyle}>›</button>
            <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} style={btnStyle}>»</button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detail && (
        <div onClick={() => setDetail(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
            borderRadius: 10, maxWidth: 700, width: '100%', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700 }}>Log Detayı</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>ID: {detail.id}</div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--text-tertiary)' }}>×</button>
            </div>
            <div style={{ padding: 18, overflow: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: '0.85rem', marginBottom: 16 }}>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Tarih:</div><div>{fmtDate(detail.created_at)}</div>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Kullanıcı:</div><div>{detail.user_name} ({detail.username})</div>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Modül:</div><div>{tableLabel(detail.table_name, detail)}</div>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>İşlem:</div>
                <div><span style={{ background: ACTION_COLORS[detail.action] + '22', color: ACTION_COLORS[detail.action], padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>{ACTION_LABELS[detail.action] || detail.action}</span></div>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Kayıt:</div>
                <div>
                  {(() => {
                    const label = extractLabel(detail.new_data, detail.table_name);
                    return (
                      <>
                        {label && <div style={{ fontWeight: 600 }}>{label}</div>}
                        {detail.record_id && <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>{detail.record_id}</div>}
                        {!label && !detail.record_id && '-'}
                      </>
                    );
                  })()}
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>IP Adresi:</div><div>{detail.ip_address || '-'}</div>
                <div style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>Tarayıcı:</div><div style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{detail.user_agent || '-'}</div>
              </div>
              {detail.new_data && (
                <>
                  <div style={{
                    background: 'var(--bg-secondary)', padding: 12, borderRadius: 6,
                    border: '1px solid var(--border-secondary)', marginBottom: 12
                  }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8 }}>Kayıt Bilgileri:</div>
                    {renderBodyGrid(detail.new_data)}
                  </div>
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Ham JSON</summary>
                    <pre style={{
                      background: 'var(--bg-secondary)', padding: 12, borderRadius: 6,
                      fontSize: '0.72rem', overflow: 'auto', maxHeight: 300,
                      border: '1px solid var(--border-secondary)', margin: '6px 0 0 0'
                    }}>{prettyBody(detail.new_data)}</pre>
                  </details>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '7px 10px',
  border: '1px solid var(--border-secondary)', borderRadius: 8,
  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
  fontSize: '0.82rem', boxSizing: 'border-box'
};

const btnStyle = {
  padding: '6px 12px', border: '1px solid var(--border-secondary)',
  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
  borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem'
};

const thStyle = {
  padding: '10px 12px', textAlign: 'left',
  fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--text-tertiary)', textTransform: 'uppercase',
  letterSpacing: '0.03em'
};

const tdStyle = { padding: '10px 12px', verticalAlign: 'top' };

export default AuditLogPage;
